import React, { useCallback, useEffect } from "react";
import Text from "@components/shared/text";
import Breadcrumb from "@components/shared/breadcrumb";
import CategoryArticles from "@components/blog/categoryArticles";
import CustomSelect from "@components/shared/customSelect";
import MetaTags from "@components/shared/metaTags";
import LastArticles from "@components/blog/lastArticles";
import { GQLCategories, GQLPosts } from "@gql/types/gql";
import { GetStaticPropsContext } from "next";
import { useQuery } from "@tanstack/react-query";
import { getKeyWordsForMetaTags } from "@lib/sharedUtils";
import { GQLDomains } from "@gql/types/gql";
import { translate } from "@helpers/translationObjectFormatter";
import { ICustomDomain, TranslationsObject } from "@genericTypes/sharedTypes";
import { useRouter } from "next/router";
import initialCall from "@helpers/initial-call";
import { activePageAtom } from "@store/generalStore";
import { useSetAtom } from "jotai";
import { CategoriesList } from "@genericTypes/sharedTypes";
import NotFoundImg from "@components/category/articles/blogs/notFoundImg";

const BlogPage = () => {
    const router = useRouter();

    const { data: domainInfo } = useQuery<ICustomDomain, Error>({
        queryKey: ["domain"],
    });
    const { data: translations } = useQuery<TranslationsObject>([
        "translations",
    ]);
    const { data: lastArticles } = useQuery<GQLPosts[], Error>([
        "lastArticles",
    ]);
    const { data: categories } = useQuery<CategoriesList, Error>({
        queryKey: ["allPostsByCategoriesSlugs"],
    });

    const categoriesList = Object.values((categories as CategoriesList) ?? []);

    const foundBlogs =
        categoriesList.filter(
            (category) => (category?.posts?.length as number) > 0,
        ).length > 0;

    const setActivePage = useSetAtom(activePageAtom);
    useEffect(() => {
        setActivePage("blog");
    }, []);
    const getCategories = useCallback(() => {
        const lastArticlesId = lastArticles?.map((post) => post.id);
        const categoriesAfterFilterId: GQLCategories[] = [];
        categoriesList?.forEach((category) => {
            categoriesAfterFilterId.push({
                ...category,
                posts: category.posts
                    ?.filter(
                        (post) =>
                            !lastArticlesId?.includes((post as GQLPosts).id),
                    )
                    .slice(0, 6),
            });
        });
        const categoriesAfterFilterPostsLength = categoriesAfterFilterId.filter(
            (category) => (category.posts?.length as number) > 1,
        );
        return categoriesAfterFilterPostsLength;
    }, [categoriesList, lastArticles]);

    const arr = [
        {
            label: translate(translations, "several_general_breadcrumb_home"),
            link: "/",
            isActive: true,
            showOnMobile: true,
        },
        {
            label: translate(translations, "several_general_blogWord"),
            link: "",
            isActive: false,
            showOnMobile: true,
        },
    ];

    return (
        <>
            <MetaTags
                title={`${translate(
                    translations,
                    "several_blogPage_meta_title",
                )} | Several.com`}
                description={translate(
                    translations,
                    "several_blogPage_meta_description",
                )}
                keyWords={
                    getKeyWordsForMetaTags(
                        domainInfo?.domain as GQLDomains,
                    ) as string
                }
            />
            <section className="max-w-7xl mx-auto px-5">
                <Breadcrumb arr={arr} />
                {foundBlogs ? (
                    <>
                        <Text
                            type="mainTitle"
                            className="text-center lg:text-4xl text-xl lg:leading-[63px] mt-4 px-5 leading-7 mb-5"
                        >
                            {`${translate(
                                translations,
                                "several_general_discoverTopBlogs",
                            )} ${new Date().getFullYear()}`}
                        </Text>
                        <Text className="text-center  lg:text-xl text-base lg:leading-9 leading-7 px-2 mt-2 text-[#58616D]">
                            {translate(
                                translations,
                                "several_blogPage_fromBeautyMindfulness",
                            )}
                        </Text>
                        <div className="max-w-lg  mx-auto mt-6 flex lg:flex-row flex-col gap-3 ">
                            <div className="relative w-full mx-auto mb-10">
                                <CustomSelect
                                    className={`w-full`}
                                    placeHolder={translate(
                                        translations,
                                        "several_general_category_select_placeholder",
                                    )}
                                    autoComplete={false}
                                    onChange={(category: GQLCategories) => {
                                        void router.push(
                                            `/blog/category/${
                                                category.slug as string
                                            }`,
                                        );
                                    }}
                                    options={categoriesList
                                        ?.filter((el) => el.posts?.length)
                                        .map((el) => ({
                                            id: el.id as number,
                                            label: el.label ?? el.name ?? "",
                                            slug: el.slug ?? "",
                                        }))}
                                />
                            </div>
                        </div>
                        {lastArticles && lastArticles.length > 0 && (
                            <LastArticles lastArticles={lastArticles} />
                        )}
                        {categoriesList && categoriesList.length > 0 && (
                            <CategoryArticles categories={getCategories()} />
                        )}
                    </>
                ) : (
                    <div className=" w-fit flex flex-col   items-center  mx-auto text-center  my-20">
                        <div className=" w-[250px] lg:w-auto">
                            <NotFoundImg />
                        </div>
                        <p className="mt-10 lg:text-xl text-base  leading-5 font-medium ">
                            {translate(
                                translations,
                                "several_articlesPage_noArticlesMessage",
                            )}
                        </p>
                    </div>
                )}
            </section>
        </>
    );
};

export const getStaticProps = async (ctx: GetStaticPropsContext) => {
    return await initialCall("Blog", ctx);
};
export default BlogPage;
